import React, { useState, useEffect } from 'react'
import { Window } from '@progress/kendo-react-dialogs'
import { Fade } from '@progress/kendo-react-animation'
import WINDOW_ICON from '../../../public/assets/window.svg'
import CLOSE_DEFAULT from '../../../public/assets/window-close-default.svg'

const EmptyWindowAction = () => null

export const FloatingWindow = ({
  visible,
  onClose,
  title,
  headerIcon,
  width,
  height, // 不传时自动计算
  children,
  draggable = false,
  resizable = false,
  centerOnShow = true,
  hideMinimizeButton = false,
  hideMaximizeButton = false,
  useCustomActions = false,
  showCustomMaximizeButton = false,
  onActionInteraction = () => {},
  modal = false,
  overlayStyle,
  className = '',
}) => {
  const [position, setPosition] = useState({ top: 100, left: 100 })
  const [maximized, setMaximized] = useState(false)
  const [isMobile, setIsMobile] = useState(false)

  // 屏幕尺寸
  const screenWidth = window.innerWidth
  const screenHeight = window.innerHeight

  // 计算宽度
  const computedWidth = width
    ? width
    : Math.min(Math.max(screenWidth * 0.6, 300), 700) // 60% 屏幕宽度，限制 300~700px

  // 计算高度
  const computedHeight = height
    ? height
    : Math.min(Math.max(screenHeight * 0.6, 300), 700) // 60% 屏幕高度，限制 300~700px

  // 显示时居中
  useEffect(() => {
    if (visible && centerOnShow) {
      const top = screenHeight / 2 - computedHeight / 2
      const left = screenWidth / 2 - computedWidth / 2
      setPosition({ top, left })
    }
  }, [
    visible,
    centerOnShow,
    computedWidth,
    computedHeight,
    screenHeight,
    screenWidth,
  ])

  // 判断移动端
  useEffect(() => {
    setIsMobile(screenWidth < 768)
  }, [screenWidth])

  useEffect(() => {
    if (!visible || !modal) return

    onActionInteraction?.()
    const interactionInterval = window.setInterval(() => {
      onActionInteraction?.()
    }, 500)

    const handleModalWheel = event => {
      const target =
        event.target instanceof HTMLElement ? event.target : null
      if (!target) return

      const isInsideWindow = Boolean(target.closest('.custom-floating-window'))
      const isOnOverlay = Boolean(target.closest('.k-overlay'))

      if (isInsideWindow || isOnOverlay) {
        onActionInteraction?.()
        event.stopPropagation()
        event.stopImmediatePropagation?.()
      }

      if (isOnOverlay) {
        event.preventDefault()
      }
    }

    document.addEventListener('wheel', handleModalWheel, {
      capture: true,
      passive: false,
    })

    return () => {
      window.clearInterval(interactionInterval)
      document.removeEventListener('wheel', handleModalWheel, {
        capture: true,
      })
    }
  }, [visible, modal, onActionInteraction])

  const stopActionEvent = event => {
    onActionInteraction?.()
    event.preventDefault()
    event.stopPropagation()
    event.nativeEvent?.stopImmediatePropagation?.()
  }

  const stopWindowWheelEvent = event => {
    onActionInteraction?.()
    event.stopPropagation()
    event.nativeEvent?.stopImmediatePropagation?.()
  }

  const TitleComponent = () => {
    return (
      <div
        className={
          useCustomActions ? 'custom-floating-window-title' : undefined
        }
        onClick={event => event.stopPropagation()}
        onMouseDown={event => {
          onActionInteraction?.()
          event.stopPropagation()
        }}
        onTouchStart={event => {
          onActionInteraction?.()
          event.stopPropagation()
        }}
        onWheelCapture={stopWindowWheelEvent}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          justifyContent: useCustomActions ? 'space-between' : undefined,
          width: useCustomActions ? '100%' : undefined,
          color: '#141414',
          fontWeight: 600,
          fontSize: 18,
        }}
      >
        <span style={{ alignItems: 'center', display: 'inline-flex', gap: 10 }}>
          {headerIcon && <img src={headerIcon} alt="icon" />}
          <span>{title}</span>
        </span>
        {useCustomActions && (
          <span className="custom-floating-window-actions">
            {showCustomMaximizeButton && (
              <button
                className="custom-floating-window-action custom-floating-window-action--maximize"
                onClick={event => {
                  stopActionEvent(event)
                  setMaximized(current => !current)
                }}
                onMouseDown={stopActionEvent}
                onTouchStart={stopActionEvent}
                type="button"
                aria-label={maximized ? 'Restore window' : 'Maximize window'}
              >
                <img src={WINDOW_ICON} alt="" />
              </button>
            )}
            <button
              className="custom-floating-window-action custom-floating-window-action--close"
              onClick={event => {
                stopActionEvent(event)
                onClose()
              }}
              onMouseDown={stopActionEvent}
              onTouchStart={stopActionEvent}
              type="button"
              aria-label="Close window"
            >
              <img src={CLOSE_DEFAULT} alt="" />
            </button>
          </span>
        )}
      </div>
    )
  }

  return (
    <Fade
      in={visible}
      transitionEnterDuration={200}
      transitionExitDuration={150}
    >
      {visible && (
        <Window
          className={`custom-floating-window ${className}`.trim()}
          title={<TitleComponent />}
          stage={isMobile || maximized ? 'FULLSCREEN' : undefined} // 移动端全屏
          top={position.top}
          left={position.left}
          initialWidth={computedWidth}
          initialHeight={computedHeight}
          draggable={draggable}
          resizable={resizable}
          modal={modal}
          overlayStyle={overlayStyle}
          onClose={useCustomActions ? undefined : onClose}
          minimizeButton={
            hideMinimizeButton || useCustomActions
              ? EmptyWindowAction
              : undefined
          }
          maximizeButton={
            hideMaximizeButton || useCustomActions
              ? EmptyWindowAction
              : undefined
          }
          restoreButton={
            hideMaximizeButton || useCustomActions
              ? EmptyWindowAction
              : undefined
          }
          closeButton={useCustomActions ? EmptyWindowAction : undefined}
          style={{ zIndex: 1000001, padding: 0 }}
        >
          {/* body */}
          <div
            className="custom-floating-window__body"
            onMouseDown={event => {
              onActionInteraction?.()
              event.stopPropagation()
            }}
            onTouchStart={event => {
              onActionInteraction?.()
              event.stopPropagation()
            }}
            onWheelCapture={stopWindowWheelEvent}
            style={{
              padding: 20,
              backgroundColor: '#fff',
              width: '100%',
              height: '100%',
              overflow: 'auto',
              boxSizing: 'border-box',
            }}
          >
            {children}
          </div>
        </Window>
      )}
    </Fade>
  )
}
